import React from "react";
import { useI18n } from "../lib/i18n";
import { useSettings, resolveImage } from "../lib/settings";
import { Link } from "react-router-dom";
import { useAutoTranslate, useAutoTranslateList } from "../lib/autoTranslate";

const fallbackPravatars = Array.from({ length: 90 }, (_, i) => `https://i.pravatar.cc/60?img=${(i % 70) + 1}`);

const About = ({ headingLevel = "h2" }) => {
  const { t } = useI18n();
  const { settings } = useSettings();
  const s = settings || {};
  const paragraphs = useAutoTranslateList(s.about_paragraphs || []);
  const stats = s.about_stats || [];
  // Use admin uploaded photos if present, else fall back to placeholder avatars mosaic
  const moments = (s.happy_moments && s.happy_moments.length > 0) ? s.happy_moments : fallbackPravatars;
  const useMosaicMask = moments === fallbackPravatars;
  const Heading = headingLevel;
  const aboutTitle = useAutoTranslate(s.about_title || t("about_title"));

  return (
    <section id="about" className="section bg-[var(--ri-cream)]">
      <div className="container-x grid lg:grid-cols-2 gap-16">
        <div>
          <p className="eyebrow mb-4">{s.about_eyebrow || t("about_eyebrow")}</p>
          <Heading className="font-serif-display text-[38px] md:text-[48px] leading-[1.05] max-w-[520px]">
            {aboutTitle}
          </Heading>
          <div className="mt-8 space-y-5 text-[17px] leading-[1.7] text-[var(--ri-ink-soft)] max-w-[560px]">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            {paragraphs.length === 0 && (
              <p>
                Real Istanbul Tours is an Istanbul tour company. Since 2014 we have run small-group walking tours through the Old City, the Grand Bazaar and the neighbourhoods across the Bosphorus.
              </p>
            )}
            <p>
              <Link to="/tours" className="link-underline text-[var(--ri-red)] font-medium">
                Browse our most popular tours →
              </Link>
            </p>
          </div>

          {stats.length > 0 && (
            <div className="mt-10 pt-8 border-t border-[var(--ri-border)] grid grid-cols-3 gap-6 max-w-[560px]">
              {stats.map((st, i) => (
                <div key={i}>
                  <div className="font-serif-display text-[30px] md:text-[36px] leading-none">{st.value}</div>
                  <div className="eyebrow mt-2 text-[var(--ri-muted)]">{st.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          {useMosaicMask ? (
            <div
              className="mx-auto"
              style={{
                width: "min(520px, 100%)",
                display: "grid",
                gridTemplateColumns: "repeat(15, 1fr)",
                gap: "4px",
                WebkitMaskImage: "radial-gradient(circle at center, black 55%, transparent 78%)",
                maskImage: "radial-gradient(circle at center, black 55%, transparent 78%)",
              }}
            >
              {moments.map((src, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-[2px] bg-cover bg-center"
                  style={{ backgroundImage: `url(${src})` }}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mx-auto" style={{ width: "min(520px, 100%)" }}>
              {moments.slice(0, 9).map((src, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-lg overflow-hidden bg-cover bg-center shadow-md"
                  style={{ backgroundImage: `url(${resolveImage(src)})` }}
                />
              ))}
            </div>
          )}
          <p className="text-center mt-4 text-[13px] text-[var(--ri-muted)]">{s.happy_moments_title || "Happy moments from our tours."}</p>
        </div>
      </div>
    </section>
  );
};

export default About;
